import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import "./Survey.css"; // or your styling

// REPLACE with your real Stripe public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

/**
 * Calculate a linear TOTAL cost from $5 at 10 recipients to $200 at 1000 recipients.
 * Then derive the per-recipient price from total / count.
 */
const calculatePrice = (recipientCount) => {
  const minCount = 10;
  const maxCount = 1000;
  const totalAtMin = 5;    // total cost at minCount
  const totalAtMax = 200;  // total cost at maxCount

  // Optionally clamp the recipientCount so the price doesn't go below or above
  // the linear range you've defined:
  let n = Math.max(minCount, Math.min(maxCount, recipientCount));

  // slope for total from n=10 -> n=1000
  const slope = (totalAtMax - totalAtMin) / (maxCount - minCount); 
  // intercept so T(10) = 5
  const intercept = totalAtMin - slope * minCount;

  // total cost is linear in n
  const totalPriceNum = slope * n + intercept;

  // derive price-per-recipient
  const pricePerRecipientNum = totalPriceNum / n;

  // If the original recipientCount is outside the clamp range,
  // recalculate total based on clamped range but scaled up/down proportionally:
  // (optional behavior; remove or tweak if you strictly want $5 to $200 only between 10..1000)
  // For example, if recipientCount < 10, you might scale linearly from 0 recipients -> 10 recipients
  // or just use a flat $5, etc.
  // For now, let's keep the simple approach: total = T(10) if n<10, or T(1000) if n>1000.

  return {
    totalPrice: totalPriceNum.toFixed(2),
    pricePerRecipient: pricePerRecipientNum.toFixed(2),
  };
};

function PaymentModal({ survey, onClose, recipientCount }) {
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Calculate the total price and price per recipient
  const { totalPrice, pricePerRecipient } = calculatePrice(recipientCount);

  // Create a PaymentIntent on mount
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`/surveys/${survey.id}/create-payment-intent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ recipientCount, totalPrice }),
        });
        if (!res.ok) {
          const errData = await res.json();
          setError(errData.error || "Error creating payment intent");
          setLoading(false);
          return;
        }
        const data = await res.json();
        setClientSecret(data.clientSecret);
        setLoading(false);
      } catch (err) {
        console.error("Error creating payment intent:", err);
        setError("Server error creating payment intent");
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, [survey.id, recipientCount, totalPrice]);

  if (error) {
    return (
      <div className="modal-backdrop">
        <div className="modal-content">
          <h2>Payment Error</h2>
          <p style={{ color: "red" }}>{error}</p>
          <button onClick={() => onClose(false)}>Close</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-content">Loading payment info...</div>
      </div>
    );
  }

  // Define an appearance object that sets the base font size to 16px
  const appearance = {
    theme: "stripe",
    variables: {
      fontSizeBase: "16px", // ensures iOS won't auto-zoom when tapping fields
    },
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2>Pay for Survey Link</h2>
        <p>
          Completing payment of ${totalPrice} for {recipientCount} recipients (
          ${pricePerRecipient} per recipient at this volume).
        </p>

        <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
          <PaymentForm onClose={onClose} />
        </Elements>
      </div>
    </div>
  );
}

function PaymentForm({ onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Optional: set a return URL after successful payment
        return_url: "https://anonysis.io/survey",
      },
    });

    if (error) {
      setErrorMsg(error.message);
    } else {
      // Notify the parent that the payment was successful
      onClose(true);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMsg && <p style={{ color: "red" }}>{errorMsg}</p>}
      <button className="logout-btn" style={{ marginTop: "15px" }} disabled={!stripe}>
        Pay Now
      </button>
      <button
        className="logout-btn"
        type="button"
        onClick={() => onClose(false)}
        style={{ marginLeft: "10px" }}
      >
        Cancel
      </button>
    </form>
  );
}

export default PaymentModal;
